/**
 * Reading and writing site tags (ADR-0034). A tag holds the signed text
 * payload `PIQ1.<id>.<mac>`, printed as a QR code or written to an NFC tag as
 * an NDEF text record. QR is read from the camera (getUserMedia + jsQR, works
 * in the PWA and the store apps); NFC uses the Capacitor plugin on native and
 * Web NFC in browsers that have it (Chrome on Android).
 */
import { isNative } from '../device';

export const TAG_PREFIX = 'PIQ1.';

/** Looks like one of our tags (the server checks the signature). */
export function isTagPayload(text: string | null | undefined): text is string {
  return !!text && text.startsWith(TAG_PREFIX) && text.length <= 200 && /^[A-Za-z0-9._-]+$/.test(text);
}

// ---- NDEF text records ------------------------------------------------------------

/** An NDEF well-known text record ("T"), UTF-8, language "en". */
export function textRecord(text: string) {
  const lang = [...new TextEncoder().encode('en')];
  const body = [...new TextEncoder().encode(text)];
  return { tnf: 1, type: [0x54], id: [] as number[], payload: [lang.length, ...lang, ...body] };
}

/** The text of the first text record (or a URI record, for tags written by other tools). */
export function textFromRecords(records: { tnf: number; type: number[]; payload: number[] }[] | null | undefined): string | null {
  for (const r of records ?? []) {
    if (r.tnf !== 1 || r.type.length !== 1) continue;
    const bytes = Uint8Array.from(r.payload);
    if (r.type[0] === 0x54 && bytes.length > 0) {
      const langLen = bytes[0]! & 0x3f;
      const utf16 = (bytes[0]! & 0x80) !== 0;
      return new TextDecoder(utf16 ? 'utf-16' : 'utf-8').decode(bytes.slice(1 + langLen));
    }
    if (r.type[0] === 0x55 && bytes.length > 0) return new TextDecoder().decode(bytes.slice(1));
  }
  return null;
}

// ---- NFC --------------------------------------------------------------------------

interface WebNdefRecord {
  recordType: string;
  data?: DataView;
  encoding?: string;
}
interface WebNdefReader {
  scan(opts?: { signal?: AbortSignal }): Promise<void>;
  write(message: string | { records: { recordType: string; data: string }[] }, opts?: { signal?: AbortSignal }): Promise<void>;
  onreading: ((e: { message: { records: WebNdefRecord[] } }) => void) | null;
  onreadingerror: (() => void) | null;
}
const WebNdef = (): (new () => WebNdefReader) | undefined => (globalThis as unknown as { NDEFReader?: new () => WebNdefReader }).NDEFReader;

export async function nfcAvailable(): Promise<boolean> {
  if (isNative()) {
    try {
      const { CapacitorNfc } = await import('@capgo/capacitor-nfc');
      return (await CapacitorNfc.isSupported()).supported;
    } catch {
      return false;
    }
  }
  return !!WebNdef();
}

/** Wait for a tag and return its text. Stops when `signal` aborts. */
export async function readNfcText(signal: AbortSignal): Promise<string> {
  if (isNative()) {
    const { CapacitorNfc } = await import('@capgo/capacitor-nfc');
    return new Promise<string>((resolve, reject) => {
      let done = false;
      const handles: { remove(): Promise<void> }[] = [];
      const finish = (fn: () => void) => {
        if (done) return;
        done = true;
        handles.forEach((h) => void h.remove());
        void CapacitorNfc.stopScanning().catch(() => {});
        fn();
      };
      signal.addEventListener('abort', () => finish(() => reject(new DOMException('Aborted', 'AbortError'))));
      void (async () => {
        handles.push(
          await CapacitorNfc.addListener('nfcEvent', (e) => {
            const text = textFromRecords(e.tag.ndefMessage);
            if (text) finish(() => resolve(text));
          }),
        );
        handles.push(await CapacitorNfc.addListener('nfcSessionEnd', () => finish(() => reject(new Error('NFC_SESSION_ENDED')))));
        await CapacitorNfc.startScanning({ invalidateAfterFirstRead: true });
      })().catch((e) => finish(() => reject(e)));
    });
  }
  const Reader = WebNdef();
  if (!Reader) throw new Error('NFC_UNAVAILABLE');
  const reader = new Reader();
  return new Promise<string>((resolve, reject) => {
    reader.onreading = (e) => {
      for (const r of e.message.records) {
        if ((r.recordType === 'text' || r.recordType === 'url') && r.data) {
          resolve(new TextDecoder(r.encoding ?? 'utf-8').decode(r.data));
          return;
        }
      }
    };
    reader.onreadingerror = () => {};
    reader.scan({ signal }).catch(reject);
    signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')));
  });
}

/** Write `text` to the next tag held to the phone. */
export async function writeNfcText(text: string, signal: AbortSignal): Promise<void> {
  if (isNative()) {
    const { CapacitorNfc } = await import('@capgo/capacitor-nfc');
    return new Promise<void>((resolve, reject) => {
      let done = false;
      const handles: { remove(): Promise<void> }[] = [];
      const finish = (fn: () => void) => {
        if (done) return;
        done = true;
        handles.forEach((h) => void h.remove());
        void CapacitorNfc.stopScanning().catch(() => {});
        fn();
      };
      signal.addEventListener('abort', () => finish(() => reject(new DOMException('Aborted', 'AbortError'))));
      void (async () => {
        handles.push(
          await CapacitorNfc.addListener('nfcEvent', () => {
            CapacitorNfc.write({ records: [textRecord(text)], allowFormat: true }).then(
              () => finish(resolve),
              (e) => finish(() => reject(e)),
            );
          }),
        );
        await CapacitorNfc.startScanning({ invalidateAfterFirstRead: false });
      })().catch((e) => finish(() => reject(e)));
    });
  }
  const Reader = WebNdef();
  if (!Reader) throw new Error('NFC_UNAVAILABLE');
  await new Reader().write({ records: [{ recordType: 'text', data: text }] }, { signal });
}

// ---- QR from the camera -----------------------------------------------------------

/**
 * Show the rear camera in `video` and resolve with the first QR code that
 * looks like a tag. Other QR codes are reported through `onOther` and ignored.
 */
export async function scanQr(video: HTMLVideoElement, signal: AbortSignal, onOther?: (text: string) => void): Promise<string> {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('CAMERA_UNAVAILABLE');
  const [{ default: jsQR }, stream] = await Promise.all([
    import('jsqr'),
    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false }),
  ]);
  const stop = () => stream.getTracks().forEach((t) => t.stop());
  if (signal.aborted) {
    stop();
    throw new DOMException('Aborted', 'AbortError');
  }
  video.srcObject = stream;
  video.setAttribute('playsinline', 'true');
  await video.play().catch(() => {});
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  let lastOther = '';
  return new Promise<string>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const end = () => {
      if (timer) clearTimeout(timer);
      stop();
      video.srcObject = null;
    };
    signal.addEventListener('abort', () => {
      end();
      reject(new DOMException('Aborted', 'AbortError'));
    });
    const tick = () => {
      if (signal.aborted) return;
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (ctx && w && h) {
        const scale = Math.min(1, 720 / Math.max(w, h));
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
        if (code?.data) {
          if (isTagPayload(code.data)) {
            end();
            resolve(code.data);
            return;
          }
          if (code.data !== lastOther) {
            lastOther = code.data;
            onOther?.(code.data);
          }
        }
      }
      timer = setTimeout(tick, 150);
    };
    tick();
  });
}
